import React, { useCallback } from 'react';
import Form from 'react-jsonschema-form';
import { Button } from 'reactstrap';
import ReactDOM from 'react-dom';
import axios from 'axios';
import moment from 'moment-timezone';

import MeterPointSelector from '../../../MeterPointSelector/MeterPointSelector';
import {
  changeOfAnnualConsumptionSchema,
  changeOfAnnualConsumptionUISchema,
} from '../../schemas/changeOfAnnualConsumptionSchema';
import { useProcessForm } from '../../hooks/useProcessForm';
import ExpiringAlert from '../../../ExpiringAlert';
import Loading from '../../../Loading/Loading';

const ChangeOfAnnualConsumptionForm = ({ onClose, initialData }) => {
  const { formContext, updateFormContext, isLoading, setIsLoading } = useProcessForm(initialData || {});

  const handleChange = useCallback(
    ({ formData }) => {
      updateFormContext(formData);
    },
    [updateFormContext]
  );

  const transformErrors = useCallback((errors) => {
    return errors.map((error) => {
      switch (error.property) {
        case '.mpan_core':
          return { ...error, message: 'MPAN Core is a required property' };
        case '.notification_efd':
          return { ...error, message: 'Notification Effective From Date is a required property' };
        case '.annual_consumption':
          return { ...error, message: 'Annual Consumption is a required property' };
        default:
          return error;
      }
    });
  }, []);

  const formatConsumption = (value) => {
    if (value === null || value === undefined) return null;
    const strValue = String(value);
    const moreThanThreeNonZero = strValue.match(/\.\d{0,3}[1-9]\d*/);
    const decimalPart = strValue.includes('.') ? strValue.split('.')[1] : '';
    if (decimalPart.length > 3 && moreThanThreeNonZero) {
      return strValue;
    }
    return parseFloat(value).toFixed(3);
  };

  const validateConsumption = (value) => {
    if (value === null || value === undefined) return false;
    const strValue = String(value);
    const decimalMatch = strValue.match(/^-?\d{1,11}(\.\d{3})$/);
    return decimalMatch !== null;
  };

  const formatToISO = (dateTimeString) => {
    if (!dateTimeString) return null;

    const hasTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(dateTimeString);
    const parsedDate = hasTimezone
      ? moment.parseZone(dateTimeString)
      : moment.tz(dateTimeString, 'Europe/London');

    if (!parsedDate.isValid()) return null;

    return parsedDate.tz('Europe/London').format('YYYY-MM-DDTHH:mm:ssZ');
  };

  const customValidate = (formData, errors) => {
    if (formData.annual_consumption !== undefined && formData.annual_consumption !== null) {
      const formatted = formatConsumption(formData.annual_consumption);
      if (!validateConsumption(formatted)) {
        errors.annual_consumption.addError('Must have exactly 3 decimal places (e.g., 55.123)');
      }
    }
    return errors;
  };

  const transformFormDataToPayload = (formData) => {
    const payload = {
      mpan_core: formData.mpan_core,
      notification_efd: formatToISO(formData.notification_efd),
      annual_consumption: formatConsumption(formData.annual_consumption),
    };

    if (formData.notification_expiry) {
      payload.notification_expiry = formatToISO(formData.notification_expiry);
    }

    if (formData.additional_information && formData.additional_information.trim()) {
      payload.additional_information = formData.additional_information.trim();
    }

    return payload;
  };

  const uiSchemaWithWidgets = {
    ...changeOfAnnualConsumptionUISchema,
    mpan_core: {
      ...changeOfAnnualConsumptionUISchema.mpan_core,
      'ui:widget': (props) => (
        <MeterPointSelector {...props} placeholder='Select MPAN' statusFilter='L' includeAllStatuses={false} />
      ),
    },
  };

  return (
    <div className='change-of-annual-consumption-form'>
      <Form
        schema={changeOfAnnualConsumptionSchema}
        uiSchema={uiSchemaWithWidgets}
        formContext={formContext}
        formData={formContext}
        transformErrors={transformErrors}
        validate={customValidate}
        showErrorList={false}
        onSubmit={({ formData }) => {
          setIsLoading(true);
          const payload = transformFormDataToPayload(formData);

          axios
            .post('/publishMessage', payload, {
              headers: {
                Authorization: `Bearer ${localStorage.token}`,
              },
              params: { queue: 'BOP.Dataflows.AnnualConsumptionChange' },
            })
            .then((response) => {
              setIsLoading(false);
              if (response.data === 'Message sent') {
                ReactDOM.render(
                  <ExpiringAlert color='success' message='Change Annual Consumption request submitted successfully!' />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
                onClose();
              } else {
                ReactDOM.render(
                  <ExpiringAlert color='danger' message={response.data.message || 'Error submitting request'} />,
                  document.getElementById('alert').appendChild(document.createElement('div'))
                );
              }
            })
            .catch((error) => {
              setIsLoading(false);
              // eslint-disable-next-line no-console
              console.error('Error submitting change annual consumption request:', error);
              ReactDOM.render(
                <ExpiringAlert color='danger' message='Error submitting request. Request logged to console.' />,
                document.getElementById('alert').appendChild(document.createElement('div'))
              );
            });
        }}
        onChange={handleChange}
      >
        <div>
          <Button type='submit' disabled={isLoading} color='primary'>
            {isLoading && <Loading />}Submit Request
          </Button>
          &nbsp;&nbsp;&nbsp;
          <Button type='button' onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default ChangeOfAnnualConsumptionForm;
